// In-memory cache for admin GET responses. Lives only for the current page
// session (clears on hard reload). Used for two things:
//   1. Synchronous seeding on mount (`cached.*`), so a screen can render
//      instantly with last-known data before its background refresh lands.
//   2. Side-effect population (`cacheSet.*`), called by api.ts whenever a
//      real fetch returns -- keeps the store fresh without callers having
//      to choose between "cached" and "live" variants.
//
// Pure storage only -- the prefetch helpers live in api.ts to avoid an
// import cycle with the `api` object.
import type {
  AdminRegistrationResponse,
  EventRecord,
  Feedback,
  LinkRecord,
} from "./api";

type Entry<T> = { value: T; at: number };

const store = {
  links: null as Entry<LinkRecord[]> | null,
  registration: new Map<string, Entry<AdminRegistrationResponse>>(),
  events: new Map<string, Entry<EventRecord[]>>(),
  feedback: new Map<string, Entry<Feedback | null>>(),
};

export const cacheSet = {
  links: (links: LinkRecord[]) => {
    store.links = { value: links, at: Date.now() };
  },
  registration: (id: string, value: AdminRegistrationResponse) => {
    store.registration.set(id, { value, at: Date.now() });
  },
  events: (id: string, value: EventRecord[]) => {
    store.events.set(id, { value, at: Date.now() });
  },
  feedback: (id: string, value: Feedback | null) => {
    store.feedback.set(id, { value, at: Date.now() });
  },
};

export const cached = {
  links: () => store.links?.value ?? null,
  linkRecord: (id: string) =>
    store.links?.value.find((l) => l.id === id) ?? null,
  registration: (id: string) => store.registration.get(id)?.value ?? null,
  events: (id: string) => store.events.get(id)?.value ?? null,
  // Tri-state: undefined = unknown (cache miss), null = known absent,
  // value = known present. Matches the shape consumers already use.
  feedback: (id: string): Feedback | null | undefined => {
    const entry = store.feedback.get(id);
    return entry ? entry.value : undefined;
  },
};
